import { useState, MouseEvent } from 'react';
import { ReactComponent as MoreIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as AiIcon } from '@elementary/common-ui/assets/ai-generate-magenta.svg';
import { ReactComponent as SeverityIcon } from '@elementary/common-ui/assets/16x16/severity.svg';
import { ReactComponent as StatusIcon } from '@elementary/common-ui/assets/20x20/incident-open.svg';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as ArrowRightIcon } from '@elementary/common-ui/assets/arrow-right.svg';
import { IconButton, SvgIcon } from '@elementary/common-ui/components';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import {
  CircularProgress,
  Menu as MuiMenu,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import styled from 'styled-components';
import useIncidentTicketing from 'incidents/hooks/useIncidentTicketing';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import { Incident, IncidentDetails } from 'incidents/types';
import { SourceType } from 'shared/types';
import { getIncidentDisplayLabel } from 'incidents/utils';
import {
  extractTicketIdFromUrl,
  ticketingIconMap,
} from 'shared/components/ticketing/utils';
import IncidentTicketCreateDialog from './IncidentTicketCreateDialog';

const MENU_OPTION_VIEW_TICKET_PREFIX = 'view-ticket-';
const MENU_OPTION_CREATE_TICKET_PREFIX = 'create-ticket-';

const IconButtonStyled = styled(IconButton)`
  padding: 6px;
`;

const NestedMenuItem = styled(MenuItem)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 150px;
  padding: 8px 16px;
  background: ${({ theme, $active }) =>
    $active ? theme.palette.common.gray9 : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray9};
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubmenuItem = styled(MenuItem)`
  padding: 8px 16px;
  min-height: 36px;
  border-radius: 8px;
  margin: 0 4px;

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray9};
  }
`;

type Props = {
  incident: Incident;
  isAiIncidentsEnabled?: boolean;
  severityOptions?: Option[];
  statusOptions?: Option[];
  assigneeOptions?: Option[];
  disabled?: boolean;
  onChange?: (id: string, val: Partial<IncidentDetails>) => void;
  onOpenAIChat: (
    id: string,
    startMessage: string,
    displayLabel: string,
  ) => void;
};

const IncidentActionsColumnMenu = ({
  incident,
  isAiIncidentsEnabled,
  severityOptions,
  statusOptions,
  assigneeOptions,
  disabled,
  onChange,
  onOpenAIChat,
}: Props) => {
  const [mainMenuAnchor, setMainMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [severityMenuAnchor, setSeverityMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [aiMenuAnchor, setAiMenuAnchor] = useState<HTMLElement | null>(null);

  const {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    handleViewTicket,
    incidentUrl,
    isResolved,
    hasTickets,
  } = useIncidentTicketing({
    sourceType: incident.source_type,
    sourceId: incident.source_id,
    incidentId: incident.id,
    status: incident.status,
    ticketsCount: incident.tickets?.length,
  });

  const menuOptions: Option[] = [];

  // View ticket options
  if (incident.tickets?.length) {
    incident.tickets.forEach((ticket) => {
      const ticketId = extractTicketIdFromUrl(
        ticket.service_ticket_url,
        ticket.service,
        ticket.service_ticket_id,
      );
      menuOptions.push({
        value: `${MENU_OPTION_VIEW_TICKET_PREFIX}${ticket.service_ticket_id}`,
        label: ticketId ? `View ${ticketId}` : 'View ticket',
        icon: ticketingIconMap[ticket.service],
      });
    });
  }

  // Create ticket options
  if (!hasTickets && !isResolved && enabledIntegrations?.length) {
    if (enabledIntegrations.length === 1) {
      menuOptions.push({
        value: `${MENU_OPTION_CREATE_TICKET_PREFIX}${enabledIntegrations[0].id}`,
        label: 'Create ticket',
        icon: isCreatingTicket ? (
          <CircularProgress size={16} />
        ) : (
          ticketingIconMap[enabledIntegrations[0].type]
        ),
        disabled: isCreatingTicket,
      });
    } else {
      enabledIntegrations.forEach((integration) => {
        menuOptions.push({
          value: `${MENU_OPTION_CREATE_TICKET_PREFIX}${integration.id}`,
          label: `Create ${integration.name} ticket`,
          icon: isCreatingTicket ? (
            <CircularProgress size={16} />
          ) : (
            ticketingIconMap[integration.type]
          ),
          disabled: isCreatingTicket,
        });
      });
    }
  }

  const handleAiSuggestionClick = (suggestion: string) => {
    const { display_name, sub_type } = incident.additional_data;
    onOpenAIChat(
      incident.id,
      suggestion,
      getIncidentDisplayLabel(display_name, sub_type),
    );
    handleMainMenuClose();
  };

  const handleMenuChange = (value: string) => {
    if (value.startsWith(MENU_OPTION_VIEW_TICKET_PREFIX)) {
      const ticketId = value.replace(MENU_OPTION_VIEW_TICKET_PREFIX, '');
      const ticket = incident.tickets?.find(
        (t) => t.service_ticket_id === ticketId,
      );
      if (ticket) {
        handleViewTicket(ticket.service_ticket_url);
      }
    } else if (value.startsWith(MENU_OPTION_CREATE_TICKET_PREFIX)) {
      const integrationId = value.replace(MENU_OPTION_CREATE_TICKET_PREFIX, '');
      onCreateTicketClick(integrationId);
    }
  };

  const handleMainMenuOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMainMenuAnchor(e.currentTarget);
  };

  const handleMainMenuClose = () => {
    setMainMenuAnchor(null);
    setSeverityMenuAnchor(null);
    setStatusMenuAnchor(null);
    setAssigneeMenuAnchor(null);
    setAiMenuAnchor(null);
  };

  const handleSeverityChange = (severity: string) => {
    onChange?.(incident.id, {
      severity: severity as IncidentDetails['severity'],
    });
    handleMainMenuClose();
  };

  const handleStatusChange = (status: string) => {
    onChange?.(incident.id, { status: status as IncidentDetails['status'] });
    handleMainMenuClose();
  };

  const handleAssigneeChange = (assignee: string) => {
    onChange?.(incident.id, {
      assignee:
        assigneeOptions?.find((o) => o.value === assignee)?.value ||
        NO_ASSIGNEE_VALUE,
    });
    handleMainMenuClose();
  };

  const isPipelineIncident = incident.source_type === SourceType.PIPELINE;
  const isActionDisabled = disabled || incident.status === 'Resolved';
  const showActionsSubmenu = !isActionDisabled && onChange;

  const hasMenuContent =
    menuOptions.length > 0 || showActionsSubmenu || isAiIncidentsEnabled;

  if (!selectedEnvId || !hasMenuContent) {
    return null;
  }

  return (
    <>
      <IconButtonStyled onClick={handleMainMenuOpen}>
        <MoreIcon />
      </IconButtonStyled>

      <MuiMenu
        anchorEl={mainMenuAnchor}
        open={Boolean(mainMenuAnchor)}
        onClose={handleMainMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Severity submenu trigger */}
        {showActionsSubmenu && (
          <NestedMenuItem
            $active={Boolean(severityMenuAnchor)}
            onMouseEnter={(e) => {
              setSeverityMenuAnchor(e.currentTarget);
              setStatusMenuAnchor(null);
              setAssigneeMenuAnchor(null);
              setAiMenuAnchor(null);
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <SeverityIcon />
              </SvgIcon>
              <Typography>Set severity</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="gray4">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        )}

        {/* Assignee submenu trigger */}
        {showActionsSubmenu && (
          <NestedMenuItem
            $active={Boolean(assigneeMenuAnchor)}
            onMouseEnter={(e) => {
              setAssigneeMenuAnchor(e.currentTarget);
              setSeverityMenuAnchor(null);
              setStatusMenuAnchor(null);
              setAiMenuAnchor(null);
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <UserIcon />
              </SvgIcon>
              <Typography>Set assignee</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="gray4">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        )}

        {/* Status submenu trigger */}
        {showActionsSubmenu && (
          <NestedMenuItem
            $active={Boolean(statusMenuAnchor)}
            onMouseEnter={(e) => {
              setStatusMenuAnchor(e.currentTarget);
              setSeverityMenuAnchor(null);
              setAssigneeMenuAnchor(null);
              setAiMenuAnchor(null);
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <StatusIcon />
              </SvgIcon>
              <Typography>Set status</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="gray4">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        )}

        {/* Divider between actions and other options */}
        {showActionsSubmenu &&
          (menuOptions.length > 0 || isAiIncidentsEnabled) && (
            <Divider sx={{ my: 1 }} />
          )}

        {/* AI Investigation submenu trigger */}
        {isAiIncidentsEnabled && (
          <NestedMenuItem
            $active={Boolean(aiMenuAnchor)}
            onMouseEnter={(e) => {
              setAiMenuAnchor(e.currentTarget);
              setSeverityMenuAnchor(null);
              setStatusMenuAnchor(null);
              setAssigneeMenuAnchor(null);
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <AiIcon />
              </SvgIcon>
              <Typography>Investigate with AI</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="gray4">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        )}

        {/* Other menu options (tickets, etc.) */}
        {menuOptions.map((option) => (
          <MenuItem
            key={option.value}
            disabled={option.disabled}
            onClick={() => {
              handleMenuChange(option.value);
              handleMainMenuClose();
            }}
            sx={{ padding: '8px 16px', gap: 1 }}
            onMouseEnter={() => {
              setSeverityMenuAnchor(null);
              setStatusMenuAnchor(null);
              setAssigneeMenuAnchor(null);
              setAiMenuAnchor(null);
            }}
          >
            {option.icon}
            <Typography>{option.label}</Typography>
          </MenuItem>
        ))}
      </MuiMenu>

      {/* Severity submenu */}
      <MuiMenu
        anchorEl={severityMenuAnchor}
        open={Boolean(severityMenuAnchor)}
        onClose={() => setSeverityMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setSeverityMenuAnchor(null),
        }}
      >
        {severityOptions?.map((option) => (
          <SubmenuItem
            key={option.value}
            onClick={() => handleSeverityChange(option.value)}
          >
            {option.icon && <SvgIcon width={16}>{option.icon}</SvgIcon>}
            <Typography sx={{ ml: option.icon ? 1 : 0 }}>
              {option.label}
            </Typography>
          </SubmenuItem>
        ))}
      </MuiMenu>

      {/* Assignee submenu */}
      <MuiMenu
        anchorEl={assigneeMenuAnchor}
        open={Boolean(assigneeMenuAnchor)}
        onClose={() => setAssigneeMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto', maxHeight: 300 } },
        }}
        MenuListProps={{
          onMouseLeave: () => setAssigneeMenuAnchor(null),
        }}
      >
        <SubmenuItem onClick={() => handleAssigneeChange(NO_ASSIGNEE_VALUE)}>
          <Typography>No assignee</Typography>
        </SubmenuItem>
        {assigneeOptions
          ?.filter((o) => o.value !== NO_ASSIGNEE_VALUE)
          .map((option) => (
            <SubmenuItem
              key={option.value}
              onClick={() => handleAssigneeChange(option.value)}
            >
              <Typography>{option.label}</Typography>
            </SubmenuItem>
          ))}
      </MuiMenu>

      {/* Status submenu */}
      <MuiMenu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setStatusMenuAnchor(null),
        }}
      >
        {statusOptions?.map((option) => (
          <SubmenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
          >
            {option.icon && <SvgIcon width={16}>{option.icon}</SvgIcon>}
            <Typography sx={{ ml: option.icon ? 1 : 0 }}>
              {option.label}
            </Typography>
          </SubmenuItem>
        ))}
      </MuiMenu>

      {/* AI Investigation submenu */}
      <MuiMenu
        anchorEl={aiMenuAnchor}
        open={Boolean(aiMenuAnchor)}
        onClose={() => setAiMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setAiMenuAnchor(null),
        }}
      >
        <SubmenuItem
          onClick={() => handleAiSuggestionClick('Find the root cause')}
        >
          <Typography>Find the root cause</Typography>
        </SubmenuItem>
        {!isPipelineIncident && (
          <SubmenuItem
            onClick={() => handleAiSuggestionClick('Show downstream impact')}
          >
            <Typography>Show downstream impact</Typography>
          </SubmenuItem>
        )}
      </MuiMenu>

      {ticketDialogOpen && selectedIntegration && selectedEnvId && (
        <IncidentTicketCreateDialog
          open={ticketDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          incidentId={incident.id}
          integration={selectedIntegration}
          envId={selectedEnvId}
          incidentUrl={incidentUrl}
          isCreating={isCreatingTicket}
          error={dialogError}
        />
      )}
    </>
  );
};

export default IncidentActionsColumnMenu;
